import React from "react";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Doughnut } from "react-chartjs-2";

ChartJS.register(ArcElement, Tooltip, Legend);

type DonutChartData = {
  value: number;
  color: string;
  label: string;
  flagCount?: number;
}[];

function findFlagCountInData(label: string, rawData: DonutChartData) {
  return rawData.find((data) => data.label === label)?.flagCount;
}

export function DonutChart({
  data,
  children,
  cutout,
  enableTooltip,
}: {
  data: DonutChartData;
  children?: React.ReactNode;
  cutout: number;
  enableTooltip?: boolean;
}) {
  // Empty Donut when there's no flags
  if (data.length === 0) {
    return (
      <svg
        width="312"
        height="308"
        viewBox="0 0 78 77"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M38.5 73.15C38.5 75.2763 36.772 77.02 34.6563 76.8077C29.8036 76.3208 25.0869 74.916 20.7585 72.6686C18.8714 71.6887 18.379 69.2838 19.5423 67.5039C20.7056 65.7241 23.0835 65.2487 24.9945 66.1811C28.0361 67.6651 31.302 68.6378 34.6599 69.0597C36.7696 69.3248 38.5 71.0237 38.5 73.15Z"
          fill="#D1D5DB"
        />
        <path
          d="M18.6393 66.5368C17.3898 68.2573 14.967 68.6528 13.3799 67.2378C8.3065 62.7149 4.50271 56.9121 2.38688 50.405C0.271054 43.8979 -0.065389 36.9677 1.3777 30.3259C1.82916 28.2481 4.02122 27.143 6.04359 27.7996C8.06596 28.4563 9.14745 30.6267 8.74809 32.7152C7.77629 37.7972 8.0968 43.0642 9.7095 48.024C11.3222 52.9838 14.1607 57.4321 17.9356 60.9707C19.4868 62.4249 19.8887 64.8163 18.6393 66.5368Z"
          fill="#D1D5DB"
        />
        <path
          d="M59.8607 10.4632C61.1102 8.74271 63.533 8.34723 65.1201 9.76218C70.1935 14.2851 73.9973 20.0879 76.1131 26.595C78.2289 33.1021 78.5654 40.0323 77.1223 46.6741C76.6708 48.7519 74.4788 49.857 72.4564 49.2004C70.434 48.5437 69.3525 46.3733 69.7519 44.2848C70.7237 39.2028 70.4032 33.9358 68.7905 28.976C67.1778 24.0162 64.3393 19.5679 60.5644 16.0293C59.0132 14.5751 58.6113 12.1837 59.8607 10.4632Z"
          fill="#D1D5DB"
        />
        <path
          d="M6.52585 26.4148C4.53307 25.6732 3.50162 23.4455 4.43846 21.5367C7.22947 15.8502 11.3905 10.9211 16.5713 7.20775C21.7521 3.49441 27.7567 1.13734 34.0384 0.321015C36.147 0.047004 37.9252 1.73941 37.9874 3.8648C38.0495 5.99019 36.3695 7.73893 34.2685 8.06557C29.5202 8.80377 24.9917 10.6461 21.0571 13.4662C17.1225 16.2863 13.9231 19.983 11.6986 24.2425C10.7144 26.1272 8.51862 27.1564 6.52585 26.4148Z"
          fill="#D1D5DB"
        />
        <path
          d="M71.9742 50.5852C73.9669 51.3268 74.9984 53.5545 74.0615 55.4633C71.2705 61.1498 67.1095 66.0789 61.9287 69.7923C56.7479 73.5056 50.7433 75.8627 44.4616 76.679C42.353 76.953 40.5748 75.2606 40.5126 73.1352C40.4505 71.0098 42.1305 69.2611 44.2315 68.9344C48.9798 68.1962 53.5083 66.3539 57.4429 63.5338C61.3775 60.7137 64.5769 57.017 66.8014 52.7575C67.7856 50.8728 69.9814 49.8436 71.9742 50.5852Z"
          fill="#D1D5DB"
        />
        <path
          d="M40.5 3.85C40.5 1.7237 42.228 -0.0199541 44.3437 0.192326C49.1964 0.67922 53.9131 2.08397 58.2415 4.33142C60.1286 5.31125 60.621 7.71623 59.4577 9.49606C58.2944 11.2759 55.9165 11.7512 54.0055 10.8189C50.9639 9.33491 47.698 8.36224 44.3401 7.9403C42.2304 7.6752 40.5 5.97629 40.5 3.85Z"
          fill="#D1D5DB"
        />
      </svg>
    );
  }

  const datax = {
    labels: data.map((d) => d.label),
    datasets: [
      {
        data: data.map((d) => d.value),
        backgroundColor: data.map((d) => d.color),
        borderRadius: 50,
      },
    ],
  };

  return (
    <>
      {children}
      <Doughnut
        data={datax}
        options={{
          animation: false,
          responsive: true,
          cutout: cutout,
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              enabled: enableTooltip,
              callbacks: {
                label: function (context) {
                  if (!data[0].flagCount) return;
                  let datasetData = context.dataset.data,
                    label = context.label,
                    currentValue = context.raw as number,
                    total = 0;

                  for (let i = 0; i < datasetData.length; i++) {
                    total += datasetData[i];
                  }
                  const percentage = parseFloat(
                    (currentValue ?? (0 / total) * 100).toFixed(1)
                  );

                  const count = findFlagCountInData(label, data);

                  return `${percentage}% : Flag Count: ${count}`;
                },
              },
            },
          },
        }}
      />
    </>
  );
}
